import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { Box, Button, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import WavesurferPlayer from '@wavesurfer/react'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import Hover from 'wavesurfer.js/dist/plugins/hover.esm.js'
import { getMusicFileUrl } from './util'
import CustomLoading from './CustomLoading'

function isVideoSource(music, url) {
  const mime = music?.mimeType || music?.contentType || music?.fileType
  if (typeof mime === 'string' && mime.startsWith('video/')) return true

  // Fallback for when you only have a URL/path.
  return /\.(mp4|webm|ogv|ogg|mov|m4v|mkv)(?:[?#]|$)/i.test(url || '')
}

const WaveformTrack = memo(function WaveformTrack({
  workout,
  punches = [],
  readOnly = false,
  playing,
  onPlayingChange,
  onReady,
  onTrackFinished,
  autoPlay = false,
}) {
  const theme = useTheme()
  const videoRef = useRef(null)
  const [ready, setReady] = useState(false)
  const [wavesurfer, setWavesurfer] = useState(null)
  const [duration, setDuration] = useState(0)

  const plugins = useMemo(
    () => [
      Hover.create({
        lineColor: '#000',
        lineWidth: 2,
        labelBackground: '#000',
        labelColor: '#fff',
        labelSize: '14px',
      }),
    ],
    []
  )

  const targets = Array.isArray(workout?.punchTargets) ? workout.punchTargets : []
  const music = workout?.expand?.musicId || null
  const trackUrl = useMemo(() => getMusicFileUrl(music), [music?.id, music?.file, music])

  const hasVideo = useMemo(() => isVideoSource(music, trackUrl), [music, trackUrl])

  useEffect(() => {
    // Reset sync when the source changes.
    const video = videoRef.current
    if (video) {
      video.pause()
      video.currentTime = 0
    }
    setReady(false)
    setWavesurfer(null)
    setDuration(0)
  }, [trackUrl])

  const syncVideo = (currentTime, isPlaying) => {
    const video = videoRef.current
    if (!video) return

    video.currentTime = currentTime

    if (isPlaying && video.paused) {
      video.play().catch(() => {})
    } else if (!isPlaying && !video.paused) {
      video.pause()
    }
  }

  const handleReady = async (ws) => {
    onReady?.(ws)
    setWavesurfer(ws)
    setDuration(ws.getDuration() || 0)
    setReady(true)

    if (hasVideo) {
      const video = videoRef.current
      if (video) {
        video.currentTime = ws.getCurrentTime?.() || 0
      }
    }
  }

  const handlePlay = (ws) => {
    onPlayingChange?.(true)
    if (hasVideo) {
      syncVideo(ws?.getCurrentTime?.() ?? 0, true)
    }
  }

  const handlePause = () => {
    onPlayingChange?.(false)
    if (videoRef.current) videoRef.current.pause()
  }

  const handleFinish = () => {
    onTrackFinished?.()
    onPlayingChange?.(false)
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    } 
  }

  const togglePlayback = async () => {
    if (!wavesurfer) return

    if (wavesurfer.isPlaying()) {
      wavesurfer.pause()
      onPlayingChange?.(false)
      return
    }

    await wavesurfer.play()
    onPlayingChange?.(true)
  }

  const targetDots = targets.map((time) => ({
    time,
    left: duration > 0 ? (time / duration) * 100 : 0,
  }))

  const punchDots = punches.map((time, idx) => ({
    time,
    idx,
    left: duration > 0 ? (time / duration) * 100 : 0,
  }))

  return (
    <Box sx={{ position: 'relative' }}>
      {hasVideo && trackUrl && (
        <Box
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'black',
          }}
        >
          <Box
            ref={videoRef}
            component="video"
            src={trackUrl}
            muted
            playsInline
            preload="metadata"
            style={{
              width: '100%',
              display: 'block',
              aspectRatio: '16 / 9',
              objectFit: 'contain',
            }}
            onLoadedMetadata={() => {
              const ws = wavesurfer
              const video = videoRef.current
              if (!ws || !video) return
              video.currentTime = ws.getCurrentTime?.() || 0
            }}
          />
        </Box>
      )}

      <Box sx={{ position: 'relative', height: 140 }}>
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, visibility: ready ? 'visible' : 'hidden' }}>
          {trackUrl ? (
            <WavesurferPlayer
              interact={false}
              height={140}
              url={trackUrl}
              waveColor={theme.palette.primary.light}
              progressColor={theme.palette.primary.main}
              onReady={handleReady}
              onPlay={handlePlay}
              onPause={handlePause}
              onFinish={handleFinish}
              plugins={plugins}
            />
          ) : (
            <Box sx={{ height: 140, display: 'grid', placeItems: 'center' }}>
              <Typography color="text.secondary">Нет аудио</Typography>
            </Box>
          )}
        </Box>

        {trackUrl && !ready && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 2,
            }}
          >
            <CustomLoading />
          </Box>
        )}

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
            pointerEvents: 'none',
            visibility: ready ? 'visible' : 'hidden',
          }}
        >
          {targetDots.map((dot) => (
            <Box
              key={`target-${dot.time}`}
              sx={{
                position: 'absolute',
                left: `${dot.left}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#000',
                border: '3px solid #fff',
                outline: '1px solid black',
                boxShadow: 1,
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 4,
            pointerEvents: 'none',
            visibility: ready ? 'visible' : 'hidden',
          }}
        >
          {punchDots.map((dot) => (
            <Box
              key={`punch-${dot.time}-${dot.idx}`}
              sx={{
                position: 'absolute',
                left: `${dot.left}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: '#dd1313',
                border: '3px solid #fff',
                outline: '1px solid black',
                boxShadow: 2,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* {!readOnly && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={wavesurfer?.isPlaying?.() ? <PauseIcon /> : <PlayArrowIcon />}
            onClick={togglePlayback}
            disabled={!ready}
          >
            {wavesurfer?.isPlaying?.() ? 'Pause' : 'Play'}
          </Button>
        </Box>
      )} */}
    </Box>
  )
})

export default WaveformTrack